import { lazy, Suspense, type ReactNode } from "react";
import { Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import { EmptyState, Button, Spinner } from "@fd/ui";
import { can, useStaff, type Capability } from "./lib/staff";

// Route-level chunks keep mobile/PWA startup responsive while Workbox retains
// every built chunk for reliable subsequent launches.
const AuthCallback = lazy(() => import("./routes/AuthCallback").then((m) => ({ default: m.AuthCallback })));
const AdminShell = lazy(() => import("./routes/AdminShell").then((m) => ({ default: m.AdminShell })));
const Dashboard = lazy(() => import("./routes/Dashboard").then((m) => ({ default: m.Dashboard })));
const TaskAutomations = lazy(() => import("./routes/TaskAutomations").then((m) => ({ default: m.TaskAutomations })));
const AppAccess = lazy(() => import("./routes/AppAccess").then((m) => ({ default: m.AppAccess })));
const Clients = lazy(() => import("./routes/Clients").then((m) => ({ default: m.Clients })));
const Users = lazy(() => import("./routes/Users").then((m) => ({ default: m.Users })));
const TestClients = lazy(() => import("./routes/TestClients").then((m) => ({ default: m.TestClients })));
const ClientDetail = lazy(() => import("./routes/ClientDetail").then((m) => ({ default: m.ClientDetail })));
const RecordContract = lazy(() => import("./routes/RecordContract").then((m) => ({ default: m.RecordContract })));
const ContractSetups = lazy(() => import("./routes/ContractSetups").then((m) => ({ default: m.ContractSetups })));
const McpConnections = lazy(() => import("./routes/McpConnections").then((m) => ({ default: m.McpConnections })));
const McpConnect = lazy(() => import("./routes/McpConnections").then((m) => ({ default: m.McpConnect })));
const BackfillContract = lazy(() => import("./routes/BackfillContract").then((m) => ({ default: m.BackfillContract })));
const EditContract = lazy(() => import("./routes/EditContract").then((m) => ({ default: m.EditContract })));
const SwitchContract = lazy(() => import("./routes/SwitchContract").then((m) => ({ default: m.SwitchContract })));
const NewOffer = lazy(() => import("./routes/NewOffer").then((m) => ({ default: m.NewOffer })));
const EditOffer = lazy(() => import("./routes/EditOffer").then((m) => ({ default: m.EditOffer })));
const Offers = lazy(() => import("./routes/Offers").then((m) => ({ default: m.Offers })));
const Products = lazy(() => import("./routes/Products").then((m) => ({ default: m.Products })));
const Catalog = lazy(() => import("./routes/Catalog").then((m) => ({ default: m.Catalog })));
const EntitlementDefaults = lazy(() => import("./routes/EntitlementDefaults").then((m) => ({ default: m.EntitlementDefaults })));
const StripeLink = lazy(() => import("./routes/StripeLink").then((m) => ({ default: m.StripeLink })));
const Pipeline = lazy(() => import("./routes/Pipeline").then((m) => ({ default: m.Pipeline })));
const Renewals = lazy(() => import("./routes/Renewals").then((m) => ({ default: m.Renewals })));
const Analytics = lazy(() => import("./routes/Analytics").then((m) => ({ default: m.Analytics })));
const Webhooks = lazy(() => import("./routes/Webhooks").then((m) => ({ default: m.Webhooks })));
const SupportPortalAdmin = lazy(() => import("./routes/SupportPortalAdmin").then(m => ({ default: m.SupportPortalAdmin })));
const Settings = lazy(() => import("./routes/Settings").then((m) => ({ default: m.Settings })));
const Access = lazy(() => import("./routes/Access").then((m) => ({ default: m.Access })));
const UnmatchedEmail = lazy(() => import("./routes/UnmatchedEmail").then((m) => ({ default: m.UnmatchedEmail })));

function AppLoading() {
  return (
    <div className="fd-centered" role="status" aria-label="Loading workspace">
      <Spinner size={24} />
    </div>
  );
}

function CapabilityRoute({ capability, children }: { capability: Capability; children: ReactNode }) {
  const staff = useStaff();
  if (!can(staff, capability)) return <EmptyState icon="shield" title="Permission required" description="Ask an administrator to update your access for this action." />;
  return children;
}

/** Legacy `/accounts/:id` deep links (e.g. from older plan-change emails) → `/clients/:id`. */
function RedirectAccountToClient() {
  const { id } = useParams();
  return <Navigate to={`/clients/${id ?? ""}`} replace />;
}

function NotFound() {
  const nav = useNavigate();
  return (
    <div className="fd-centered">
      <EmptyState
        icon="search"
        title="Page not found"
        description="That route doesn’t exist in the billing workspace."
        action={<Button variant="gold" onClick={() => nav("/dashboard")}>Back to dashboard</Button>}
      />
    </div>
  );
}

export function App() {
  return (
    <Suspense fallback={<AppLoading />}>
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route element={<AdminShell />}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/task-automations" element={<TaskAutomations />} />
          <Route path="/pipeline" element={<Pipeline />} />
          <Route path="/renewals" element={<Renewals />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/clients" element={<Clients />} />
          <Route path="/users" element={<Users />} />
          <Route path="/test-clients" element={<TestClients />} />
          <Route path="/test-clients/:clientId" element={<TestClients />} />
          <Route path="/app-access" element={<AppAccess />} />
          <Route path="/clients/new" element={<CapabilityRoute capability="contracts.create"><RecordContract /></CapabilityRoute>} />
          <Route path="/contract-setups" element={<ContractSetups />} />
          <Route path="/contract-setups/:id" element={<ContractSetups />} />
          <Route path="/mcp/connections" element={<McpConnections />} />
          <Route path="/mcp/connect" element={<McpConnect />} />
          <Route path="/clients/:id/contracts/:contractId/switch" element={<CapabilityRoute capability="contracts.manage"><SwitchContract /></CapabilityRoute>} />
          <Route path="/clients/:id/contracts/:contractId/edit" element={<CapabilityRoute capability="contracts.manage"><EditContract /></CapabilityRoute>} />
          <Route path="/clients/:id/backfill/:subId" element={<CapabilityRoute capability="contracts.create"><BackfillContract /></CapabilityRoute>} />
          <Route path="/clients/:id" element={<ClientDetail />} />
          <Route path="/accounts" element={<Navigate to="/clients" replace />} />
          <Route path="/accounts/:id" element={<RedirectAccountToClient />} />
          <Route path="/offers" element={<Offers />} />
          <Route path="/offers/new" element={<NewOffer />} />
          <Route path="/offers/:id/edit" element={<EditOffer />} />
          <Route path="/products" element={<Products />} />
          <Route path="/catalog" element={<Catalog />} />
          <Route path="/entitlement-defaults" element={<EntitlementDefaults />} />
          <Route path="/webhooks" element={<Webhooks />} />
          <Route path="/support-portal" element={<SupportPortalAdmin />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/access" element={<Access />} />
          <Route path="/unmatched-email" element={<UnmatchedEmail />} />
          <Route path="/stripe-link" element={<StripeLink />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
